import { Contact } from '~api/address_book';

import { useRef } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { NavLink, Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import {
  Breadcrumb,
  Card,
  DropdownMenu,
  FormControl,
  LoadingButton,
  MoreButton,
  PageTitle,
  PageWrapper,
  formatId,
} from '@ingrid/luterkort-react';

import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { MutationSaveBar } from '~src/components/MutationSaveBar/MutationSaveBar';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';
import { Addresses } from '~src/pages/shipment/create/addresses';
import { AddressesProvider } from '~src/pages/shipment/create/addresses-context';

import { BasicInformation } from '../create/basic-information';
import { CarrierProduct } from '../create/carrier-product';
import { Metadata } from '../create/metadata';
import { useShipmentTemplateUpdateMutation } from '../mutations';
import { useTemplateDetailsQuery } from '../queries';
import { TemplateUpsert, useTemplateForm } from '../types';

export function Page() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams();

  const viewportRef = useRef(null);
  const navigate = useNavigate();

  const template = useTemplateDetailsQuery({ id });
  const methods = useTemplateForm({ defaultValues: template });
  const mutation = useShipmentTemplateUpdateMutation();

  const onSubmit: SubmitHandler<TemplateUpsert> = async values => {
    const promise = mutation.mutateAsync(values);

    await toast.promise(promise, {
      success: { title: t('shipmentTemplates.details.updateSuccess') },
      error: { title: t('shipmentTemplates.details.updateError') },
      loading: false,
    });

    navigate(`../${id}`);
  };

  return (
    <PageWrapper gap="lg" variant="center" style={{ marginBottom: MutationSaveBar.Offset }}>
      <FormProvider {...methods}>
        <FormControl.Root>
          <FormControl.Form onSubmit={methods.handleSubmit(onSubmit)} style={{ display: 'none' }} />

          <PageTitle.Wrapper ref={viewportRef}>
            <PageTitle.Stack>
              <Breadcrumb aria-label={t('common.breadcrumbs')}>
                <NavLink to="..">{t('common.templates')}</NavLink>
                <span aria-current="page">{formatId(id)}</span>
              </Breadcrumb>
            </PageTitle.Stack>

            <FormControl.SubmitButton asChild>
              <LoadingButton loading={mutation.isPending}>{t('common.save')}</LoadingButton>
            </FormControl.SubmitButton>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <MoreButton variant="secondary" />
              </DropdownMenu.Trigger>

              <DropdownMenu.Content>
                <DropdownMenu.Item asChild>
                  <RouterLink to={`../${template.id!}`}>{t('common.cancel')}</RouterLink>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </PageTitle.Wrapper>

          <MutationBanner mutation={mutation} />

          <AddressesProvider
            contacts={[
              template.contact_from,
              template.contact_to,
              template.contact_return,
              template.customer_info?.contact,
            ].filter((c): c is Contact => !!c)}
          >
            <Card.List>
              <BasicInformation />
              <Addresses />
              <CarrierProduct />
              <Metadata />
            </Card.List>
          </AddressesProvider>

          <FormLeaveBlocker formState={methods.formState} />

          <MutationSaveBar viewportRef={viewportRef}>
            <MutationSaveBar.Cancel to=".." />

            <MutationSaveBar.Submit
              onClick={methods.handleSubmit(onSubmit)}
              isLoading={mutation.isPending}
              label={t('common.save')}
            />
          </MutationSaveBar>
        </FormControl.Root>
      </FormProvider>
    </PageWrapper>
  );
}
